import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { GridCellCalculationSummary } from 'src/utils/grid/GridCellCalculation';

/** 셀 계산 결과 팝업을 표시하고 닫는 데 필요한 값입니다. */
interface GridCellCalculationPopupProps {
	/** 선택 영역에서 계산된 합계, 평균, 개수, 최소값, 최대값 */
	summary: GridCellCalculationSummary;
	/** 닫기 버튼, 배경 클릭 또는 Escape 입력 시 호출할 콜백 */
	onClose: () => void;
	/** 기존 그리드 우클릭 메뉴보다 팝업을 위에 표시하기 위한 레이어 순서 */
	zIndex: number;
}

/** 한국어 숫자 형식으로 천 단위 구분 기호와 항목별 소수점 표시 자릿수를 적용합니다. */
const formatValue = (value: number, maximumFractionDigits = 10): string => value.toLocaleString('ko-KR', {
	maximumFractionDigits,
});

/**
 * 통계 결과의 항목 구성과 팝업 본문 렌더링만 담당합니다.
 * 배경 클릭 및 키보드 이벤트 처리는 외부 GridCellCalculationPopup에서 담당하여
 * 결과 표시 UI와 팝업 제어 책임을 분리합니다.
 */
const CalculationPopupContent = ({ summary, onClose }: Omit<GridCellCalculationPopupProps, 'zIndex'>) => {
	const results = [
		{ label: '개수', value: summary.count.toLocaleString('ko-KR') },
		{ label: '합계', value: formatValue(summary.sum) },
		{ label: '평균', value: formatValue(summary.average, 2) },
		{ label: '최소값', value: formatValue(summary.min) },
		{ label: '최대값', value: formatValue(summary.max) },
	];

	return (
		<div
			className="w-[360px] max-w-[90vw] rounded-lg border border-gray-300 bg-white shadow-xl"
			// 팝업 내부 클릭이 배경의 onMouseDown까지 전달되어 닫히지 않도록 차단합니다.
			onMouseDown={event => event.stopPropagation()}
		>
			<div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
				<h3 className="text-base font-semibold text-gray-900">셀 계산 결과</h3>
				<button type="button" className="text-xl text-gray-500 hover:text-gray-800" onClick={onClose}>×</button>
			</div>
			<div className="px-5 py-4">
				{results.map(result => (
					<div key={result.label} className="flex items-center justify-between border-b border-gray-100 py-2 last:border-b-0">
						<span className="text-sm text-gray-600">{result.label}</span>
						<span className="text-sm font-semibold text-gray-900">{result.value}</span>
					</div>
				))}
			</div>
			<div className="flex justify-end border-t border-gray-200 px-5 py-3">
				<button type="button" className="rounded bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600" onClick={onClose}>
					확인
				</button>
			</div>
		</div>
	);
};

/**
 * 셀 계산 결과를 document.body 최상위 레이어에 표시하는 모달 팝업입니다.
 * Portal을 사용하므로 그리드 컨테이너의 overflow에 잘리지 않으며,
 * Escape 키, 반투명 배경 클릭, X 버튼, 확인 버튼으로 닫을 수 있습니다.
 */
export const GridCellCalculationPopup = ({
	summary,
	onClose,
	zIndex,
}: GridCellCalculationPopupProps) => {
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [onClose]);

	// 그리드 내부 stacking context와 overflow의 영향을 피하기 위해 body에 직접 렌더링합니다.
	return ReactDOM.createPortal(
		<div
			className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-20"
			style={{ zIndex }}
			// 내부 콘텐츠에서 전파를 차단하므로 여기까지 도달한 클릭은 팝업 바깥 클릭입니다.
			onMouseDown={onClose}
		>
			<CalculationPopupContent summary={summary} onClose={onClose} />
		</div>,
		document.body,
	);
};
